import RadioGroup from "designer/builder/ui/views/radio-group/radio-group";
import { GlideDropdown } from "designer/ui/smart/glide-dropdown/glide-dropdown";
import Button from "designer/ui/smart/button/button";
import { AppPlanPanelContainer } from "./app-settings-plan-style";
import {
    AppPlanCode,
    AppPlanInfo,
    displayDollars,
    getSubscriptionName,
    getPlanPrivacyName,
    getPlanTypeName,
    PlanPrivacy,
    PlanType,
    SubscriptionType,
} from "common/app-plans";
import { GlideFC } from "lib/glide-fc";
import { useCallback, useMemo } from "react";
import { ValueSlider } from "../slider/value-slider";

interface Props {
    appPlanInfo: AppPlanInfo;
    plan: PlanType | undefined;
    privacy: PlanPrivacy;
    subscription: SubscriptionType;
    numPrivateUsers: number;
    canApply: boolean;
    canCancel: boolean;
    canCancelPlan: boolean;
    privateUserDescription: string | undefined;
    onApply: () => void;
    onCancel: () => void;
    onCancelPlan: () => void;
    planChange: (plan: PlanType) => void;
    privacyChange: (privacy: PlanPrivacy) => void;
    subscriptionChange: (billing: SubscriptionType) => void;
    numPrivateUsersChange: (numPrivateUsers: number) => void;
}

interface PlanPrivacyPair {
    plan: PlanType;
    privacy: PlanPrivacy;
}

const AppPlanPanel: GlideFC<Props> = p => {
    const {
        appPlanInfo,
        plan,
        privacy,
        subscription,
        numPrivateUsers,
        canApply,
        canCancel,
        canCancelPlan,
        planChange,
        privacyChange,
        subscriptionChange,
        numPrivateUsersChange,
        onApply,
        onCancel,
        onCancelPlan,
        privateUserDescription,
    } = p;
    const { price, title, units, numberOfUsers: numberOfUsersFromPlan, code, isPrivate, isMonthly } = appPlanInfo;
    const isFree = plan === PlanType.Free;

    const showUserSelector = isPrivate && !isMonthly;
    const showBillingPeriodSelector = code !== AppPlanCode.Free;
    const isPrivatePro = code === AppPlanCode.ProPrivateAnnual || code === AppPlanCode.ProPrivateMonthly;

    const planPrivacyPairChange = useCallback(
        (planPrivacyPair: PlanPrivacyPair) => {
            planChange(planPrivacyPair.plan);
            privacyChange(planPrivacyPair.privacy);
        },
        [planChange, privacyChange]
    );

    const planPrivacyPairs: PlanPrivacyPair[] = useMemo(
        () => [
            { plan: PlanType.Free, privacy: PlanPrivacy.Public },
            { plan: PlanType.Basic, privacy: PlanPrivacy.Public },
            { plan: PlanType.Pro, privacy: PlanPrivacy.Public },
            { plan: PlanType.Pro, privacy: PlanPrivacy.Private },
        ],
        []
    );

    const selectedPlanPrivacyPair = planPrivacyPairs.find(ppp => ppp.plan === plan && ppp.privacy === privacy);

    const nameForPlanPrivacyPair = useCallback((ppp: PlanPrivacyPair) => {
        return ppp.privacy === PlanPrivacy.Private
            ? `${getPlanPrivacyName(ppp.privacy)} ${getPlanTypeName(ppp.plan)}`
            : getPlanTypeName(ppp.plan);
    }, []);

    const nameForSubscription = useCallback((sub: SubscriptionType) => getSubscriptionName(sub), []);

    const privateUsersChange = useCallback((n: number) => numPrivateUsersChange(Math.max(20, Math.floor(n / 5) * 5)), [
        numPrivateUsersChange,
    ]);

    return (
        <AppPlanPanelContainer>
            <div className="group summary">
                <div className="bold-title">{title}</div>
                <div className="cost-section">
                    <div className="number">
                        {isFree ? "Free" : displayDollars(price * (isPrivatePro ? numPrivateUsers : 1))}
                    </div>
                    {isFree ? null : (
                        <div className="label description">
                            {units}
                            <br />
                            billed {subscription === SubscriptionType.Annual ? "yearly" : "monthly"}
                        </div>
                    )}
                </div>
            </div>

            <div className="group plan-group">
                <div className="bold-title">Plan</div>
                <GlideDropdown
                    items={planPrivacyPairs}
                    selected={selectedPlanPrivacyPair}
                    descriptionForItem={nameForPlanPrivacyPair}
                    defaultDisplayLabel={"—"}
                    onItemSelect={planPrivacyPairChange}
                />
                <a
                    className="learn-more"
                    target="_blank"
                    rel="noopener noreferrer"
                    href="https://www.glideapps.com/pricing">
                    Learn more about plans
                </a>
            </div>
            {plan !== undefined && (
                <>
                    {showBillingPeriodSelector && (
                        <div className="group radio-group">
                            <div className="bold-title">Billed</div>
                            <RadioGroup
                                title="billed"
                                items={[SubscriptionType.Monthly, SubscriptionType.Annual]}
                                value={subscription}
                                getName={nameForSubscription}
                                onChange={subscriptionChange}
                            />
                        </div>
                    )}

                    {showUserSelector && (
                        <div className="group private-users">
                            <div className="bold-title">Private Users</div>
                            <div>
                                <ValueSlider
                                    min={20}
                                    max={250}
                                    step={5}
                                    value={numPrivateUsers}
                                    onSelectionChanged={privateUsersChange}
                                />
                                <div className="label">{numberOfUsersFromPlan} users per month</div>
                            </div>
                        </div>
                    )}

                    {privateUserDescription !== undefined && (
                        <div className="group">
                            <div className="bold-title">Per-user Billing</div>
                            <div className="info">
                                <div className="label">{privateUserDescription}</div>
                            </div>
                        </div>
                    )}
                </>
            )}

            {canApply ? (
                <div className="group submit">
                    <Button variant="cancel" disabled={!canCancel} onClick={onCancel}>
                        Clear Changes
                    </Button>
                    <Button variant="filled" disabled={!canApply} onClick={onApply}>
                        Apply Changes
                    </Button>
                </div>
            ) : null}

            {canCancelPlan ? (
                <div className="group cancel">
                    <Button variant="inline" disabled={!canCancelPlan} onClick={onCancelPlan}>
                        Cancel plan
                    </Button>
                </div>
            ) : null}
        </AppPlanPanelContainer>
    );
};

export default AppPlanPanel;
